import { t } from '@lingui/macro';
import { isEmpty, set, get, merge } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDataService } from '../../../services/ApiDataService';
import { useFormSaveService } from '../../../services/FormSaveService';
import { useRequestState, useSingletonValue } from '../../../utilities/Hooks';
import { MAPPINGS, FIELD_MAPPINGS, VALIDATION_FIELD_MAPPINGS } from './Constants';

const SYSTEM_CONFIG_URL = '/ipm/v1/admin/systemconfiguration';
const PASSWD_CONFIG_URL = '/ipm/public/systemconfiguration/getPasswordRule';
const LOGIN_CONFIG_URL = '/ipm/public/systemconfiguration/getLoginFailureRule';

export function useConfigurationData() {
    const [isLoading, setIsLoading] = useState();
    const [data, setData] = useState();
    const [error, setError] = useState();
    const dataService = useDataService();

    const reset = useCallback(() => setData(null), []);

    useEffect(() => {
        if (data) return;

        getData();

        async function getData() {
            try {
                setIsLoading(true);
                setError(undefined);
                setData(undefined);

                const [[systemConfig], passwdConfig, loginConfig] = await Promise.all([
                    dataService.get(SYSTEM_CONFIG_URL),
                    dataService.get(PASSWD_CONFIG_URL),
                    dataService.get(LOGIN_CONFIG_URL),
                ]);

                const responseData = {
                    [MAPPINGS.SYSTEM_CONFIG]: systemConfig,
                    [MAPPINGS.PASSWD_CONFIG]: passwdConfig,
                    [MAPPINGS.LOGIN_CONFIG]: loginConfig,

                    // "meta" fields which aren't persisted
                    [MAPPINGS.META]: {
                        smtpEnabled: !isEmpty(systemConfig?.smtp?.hostName || ''),
                        smtpTestEmail: '',

                        // store this outside of sysConfig, becuase we'll still need it if we upload a new file
                        // but the user hasn't yet clicked save...
                        geoJsonFileName: systemConfig?.geoJsonFileName,
                        geoJsonFile: systemConfig?.geoJsonFile,
                    },
                };

                if (isEmpty(get(responseData, FIELD_MAPPINGS.SMTP))) {
                    set(responseData, FIELD_MAPPINGS.SMTP, {});
                }

                assignDefaultFieldValue(
                    responseData,
                    FIELD_MAPPINGS.PASSWORD_MIN_LENGTH,
                    FIELD_MAPPINGS.DEFAULT_PASSWORD_MIN_LENGTH
                );
                assignDefaultFieldValue(
                    responseData,
                    FIELD_MAPPINGS.NUM_LOGIN_ATTEMPTS_TO_LOCK_OUT,
                    FIELD_MAPPINGS.DEFAULT_NUM_LOGIN_ATTEMPTS_TO_LOCK_OUT
                );
                assignDefaultFieldValue(
                    responseData,
                    FIELD_MAPPINGS.LOCK_OUT_TIME_MINUTES,
                    FIELD_MAPPINGS.DEFAULT_LOCK_OUT_TIME_MINUTES
                );

                setData(responseData);
            } catch (e) {
                setError(e);
            }

            setIsLoading(false);
        }
    }, [dataService, data]);

    return { data, isLoading, error, reset };
}

export function useSaveConfigurationData(configurationData, reset, setFormError) {
    const formSaveService = useFormSaveService();

    const {
        isLoading,
        data,
        methods: { onInit, onLoad },
    } = useRequestState();

    const save = useCallback(
        async (formData) => {
            onInit();

            const mergedData = merge({}, configurationData, formData);
            const saveData = mergedData[MAPPINGS.SYSTEM_CONFIG];

            onLoad(
                await formSaveService.save({
                    url: SYSTEM_CONFIG_URL,
                    recordId: saveData._id,
                    dataToSave: saveData,
                    validationFieldMapper: (key) => {
                        // return false for fields that do not exist in our mapping
                        return get(VALIDATION_FIELD_MAPPINGS, key, false);
                    },
                    setError: (e, d) => {
                        setFormError(e, d);
                    },
                })
            );

            reset();
        },
        [configurationData, formSaveService, onInit, onLoad, reset, setFormError]
    );

    return {
        save,
        isLoading,
        data,
    };
}

function assignDefaultFieldValue(data, fieldMapping, defaultValueMapping) {
    const defaultValue = get(data, defaultValueMapping);
    const dataValue = get(data, fieldMapping, defaultValue);

    set(data, fieldMapping, dataValue);
}

export function useConfigBreadcrumbs() {
    return useSingletonValue(() => [
        {
            text: t`General`,
        },
    ]);
}
